import { ProviderAccount } from '@prisma/client'
import { message } from 'antd'
import { ComponentProps, useState } from 'react'
import { useUserContext } from '~/core/context'
import { Api } from '~/core/trpc'
import { ProviderClient } from '../../providers/abstract/provider.client'
import { ModalAuthentication } from '../components/ModalAuthentication'
import { useStrategyNango } from '../strategy/useStrategyNango'
import { useStrategyOauth2 } from '../strategy/useStrategyOauth2'

type Props = {
  provider: ProviderClient
  providerAccount?: ProviderAccount
  providerAccounts?: ProviderAccount[]
  scopes?: string[]
  extraAuthParams?: Record<string, any>
  onSubmit: (providerAccount: ProviderAccount) => void
}

type PropsModal = ComponentProps<typeof ModalAuthentication>

export const useAuthentication = ({
  provider,
  providerAccount,
  providerAccounts,
  scopes,
  extraAuthParams,
  onSubmit,
}: Props) => {
  const [isModalOpen, setModalOpen] = useState(false)

  const { session } = useUserContext()

  const nango = useStrategyNango()
  const oauth2 = useStrategyOauth2()

  const claim = Api.dataConnector.claimAccessToken.useMutation()

  const createAccount = Api.providerAccount.create.useMutation()

  const updateAccount = Api.providerAccount.update.useMutation()

  const idsExternalExisting = providerAccounts
    ?.map(account => account.idExternal)
    .filter(Boolean)

  const updateOrCreateAccount = async (values: Partial<ProviderAccount>) => {
    const userId = session?.user?.id
    const idExternal = values.idExternal
    const idExisting = idsExternalExisting?.find(
      candidate => candidate === idExternal,
    )

    if (idExisting) {
      const account = providerAccounts?.find(
        account => account.idExternal === idExisting,
      )

      return updateAccount.mutateAsync({
        where: { id: account.id },
        data: {
          name: values.name,
          pictureUrl: values.pictureUrl,
          configuration: values.configuration,
          connection: values.connection,
          status: 'ACTIVE',
        },
      })
    } else {
      return createAccount.mutateAsync({
        data: {
          providerKey: provider.key,
          userId,
          name: values.name,
          pictureUrl: values.pictureUrl,
          configuration: values.configuration,
          connection: values.connection,
          idExternal,
        },
      })
    }
  }

  const handleNango = async () => {
    try {
      const response = await nango.start(provider)

      const values = await claim.mutateAsync({
        providerKey: provider.key,
        payload: response,
      })

      const accountUpdated = await updateOrCreateAccount(values)

      onSubmit(accountUpdated)
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    }
  }

  const handleOauth2 = async (input?: any) => {
    try {
      const response = await oauth2.start(provider, {
        input,
        scopes,
        extraParams: extraAuthParams,
      })

      const values = await claim.mutateAsync({
        providerKey: provider.key,
        payload: response,
      })

      const accountUpdated = await updateOrCreateAccount(values)

      onSubmit(accountUpdated)
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    }
  }

  const handleForm = async (input?: any) => {
    try {
      const values = await claim.mutateAsync({
        providerKey: provider.key,
        payload: { input },
      })

      const accountUpdated = await updateOrCreateAccount(values)

      onSubmit(accountUpdated)
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    }
  }

  const start = async (input?: any) => {
    const isNango = provider.authentication?.type === 'nango'
    const isOauth2 = provider.authentication?.type === 'oauth2'
    const isForm =
      provider.authentication?.type === 'form' ||
      provider.authentication?.inputs?.length > 0

    if (isForm) {
      setModalOpen(true)
      return
    }

    if (isNango) {
      await handleNango()
      return
    }

    if (isOauth2) {
      await handleOauth2(input)
      return
    }
  }

  const propsModal: PropsModal = {
    isOpen: isModalOpen,
    provider: provider,
    providerAccount: providerAccount,
    onSubmitInput: async values => {
      const isOauth2 = provider.authentication?.type === 'oauth2'
      const isForm = provider.authentication?.type === 'form'

      if (isOauth2) {
        setModalOpen(false)

        await handleOauth2(values)
        return
      }

      if (isForm) {
        setModalOpen(false)

        await handleForm(values)
        return
      }
    },
    onSubmit: async account => {
      try {
        const accountUpdated = await updateOrCreateAccount(account)

        onSubmit(accountUpdated)

        setModalOpen(false)
      } catch (error) {
        message.error(`Something went wrong`)
        console.error('Unknown Error', error)
      }
    },
    onCancel: () => {
      setModalOpen(false)
    },
  }

  return {
    start,
    propsModal,
  }
}
